import { useState } from "react";
import { useBusinessMetrics } from "@/hooks/queries/useBusinessMetrics";

const TIER_LABELS: Record<string, string> = {
  seedling:    "Seedling",
  ember:       "Ember",
  keeper:      "Keeper",
  storyteller: "Storyteller",
  tribe:       "Tribe",
};

const TIER_ORDER = ["seedling", "ember", "keeper", "storyteller", "tribe"];

const OCCASION_LABELS: Record<string, string> = {
  new_baby:  "New Baby",
  birthday:  "Birthday",
  holiday:   "Holiday",
  general:   "General",
};

function StatCard({
  label,
  value,
  sub,
}: {
  label: string;
  value: number | string;
  sub?: string;
}) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
        {label}
      </p>
      <p className="text-3xl font-bold text-gray-900">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

function RateCard({
  label,
  value,
  sub,
  greenThreshold,
  amberThreshold,
}: {
  label: string;
  value: number | null;
  sub?: string;
  greenThreshold: number;
  amberThreshold: number;
}) {
  const display = value === null ? "—" : `${value}%`;
  const colorClass =
    value === null
      ? "text-gray-400"
      : value >= greenThreshold
      ? "text-green-600"
      : value >= amberThreshold
      ? "text-amber-500"
      : "text-red-500";

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
        {label}
      </p>
      <p className={`text-3xl font-bold ${colorClass}`}>{display}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

export default function BusinessDashboard() {
  const { data, isLoading, isError, error, refetch } = useBusinessMetrics();
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const handleRefresh = async () => {
    setLastUpdated(new Date());
    await refetch();
  };

  function formatLastUpdated(date: Date): string {
    const diffMs = Date.now() - date.getTime();
    const diffMin = Math.floor(diffMs / 60_000);
    if (diffMin < 1) return "just now";
    if (diffMin === 1) return "1 minute ago";
    return `${diffMin} minutes ago`;
  }

  const pct = (n: number, total: number) =>
    total === 0 ? "0%" : `${Math.round((n / total) * 100)}%`;

  const totalActiveUsers = data
    ? Object.values(data.by_tier).reduce((a, b) => a + b, 0)
    : 0;

  const sortedTiers = data
    ? TIER_ORDER.filter((t) => data.by_tier[t] !== undefined).concat(
        Object.keys(data.by_tier).filter((t) => !TIER_ORDER.includes(t))
      )
    : [];

  const PAYING_TIERS = new Set(["keeper", "storyteller", "tribe", "ember"]);

  return (
    <div className="p-6 max-w-5xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">Business</h1>
          {lastUpdated && (
            <p className="text-xs text-gray-400 mt-0.5">
              Last updated: {formatLastUpdated(lastUpdated)}
            </p>
          )}
        </div>
        <button
          onClick={handleRefresh}
          disabled={isLoading}
          className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
        >
          {isLoading ? "Loading..." : "Refresh"}
        </button>
      </div>

      {/* Error */}
      {isError && error && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-sm font-medium text-red-700">Failed to load</p>
          <p className="text-xs text-red-500 mt-0.5">{error.message}</p>
        </div>
      )}

      {/* Loading skeleton */}
      {isLoading && !data && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-lg border border-gray-200 p-5 h-24 animate-pulse"
              >
                <div className="h-2 bg-gray-100 rounded w-16 mb-3" />
                <div className="h-8 bg-gray-100 rounded w-12" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Data */}
      {data && (
        <div className="space-y-6">
          {/* Row 1 — headline numbers */}
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <StatCard
              label="Paying users"
              value={data.total_paying_users}
              sub="active non-seedling"
            />
            <RateCard
              label="Conversion rate"
              value={data.conversion_rate}
              sub="free → paid, all time"
              greenThreshold={10}
              amberThreshold={5}
            />
            <StatCard
              label="Upgrades this month"
              value={data.upgrades_this_month}
            />
            <StatCard
              label="Avg days to convert"
              value={data.avg_days_to_convert === null ? "—" : data.avg_days_to_convert}
              sub="seedling → paid"
            />
          </div>

          {/* Row 2 — tier breakdown */}
          <div>
            <h2 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
              Active subscriptions by tier
            </h2>
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Tier</th>
                    <th>Users</th>
                    <th>% of all active</th>
                    <th style={{ width: "40%" }}>Distribution</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedTiers.map((tier) => {
                    const count = data.by_tier[tier] ?? 0;
                    const isPaying = PAYING_TIERS.has(tier);
                    return (
                      <tr key={tier}>
                        <td>
                          <span
                            className={
                              isPaying
                                ? "font-semibold text-orange-700"
                                : "text-gray-500"
                            }
                          >
                            {TIER_LABELS[tier] ?? tier}
                          </span>
                        </td>
                        <td
                          className={
                            isPaying
                              ? "text-gray-900 font-bold"
                              : "text-gray-900 font-medium"
                          }
                        >
                          {count}
                        </td>
                        <td className="text-gray-500">
                          {pct(count, totalActiveUsers)}
                        </td>
                        <td>
                          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${
                                isPaying ? "bg-orange-400" : "bg-gray-300"
                              }`}
                              style={{
                                width: pct(count, totalActiveUsers),
                              }}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Row 3 — gifts */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* Left: gift overview */}
            <div className="bg-white rounded-lg border border-gray-200 p-5">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-4">
                Gift overview
              </p>
              <div className="space-y-3">
                <div className="flex justify-between items-baseline">
                  <span className="text-sm text-gray-600">Sent (all time)</span>
                  <span className="text-xl font-bold text-gray-900">
                    {data.gifts_sent}
                  </span>
                </div>
                <div className="flex justify-between items-baseline">
                  <span className="text-sm text-gray-600">Sent this month</span>
                  <span className="text-xl font-bold text-gray-900">
                    {data.gifts_sent_this_month}
                  </span>
                </div>
                <div className="flex justify-between items-baseline">
                  <span className="text-sm text-gray-600">Redeemed (all time)</span>
                  <span className="text-xl font-bold text-gray-900">
                    {data.gifts_redeemed}
                  </span>
                </div>
                <div className="flex justify-between items-baseline border-t border-gray-100 pt-3">
                  <span className="text-sm text-gray-600">Redemption rate</span>
                  <span
                    className={`text-xl font-bold ${
                      data.gift_redemption_rate === null
                        ? "text-gray-400"
                        : data.gift_redemption_rate >= 70
                        ? "text-green-600"
                        : data.gift_redemption_rate >= 40
                        ? "text-amber-500"
                        : "text-red-500"
                    }`}
                  >
                    {data.gift_redemption_rate === null
                      ? "—"
                      : `${data.gift_redemption_rate}%`}
                  </span>
                </div>
              </div>
            </div>

            {/* Right: by occasion + tier */}
            <div className="bg-white rounded-lg border border-gray-200 p-5">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-4">
                Gifts by occasion &amp; tier
              </p>
              {data.gifts_by_occasion && Object.keys(data.gifts_by_occasion).length > 0 ? (
                <div className="mb-4">
                  <p className="text-xs text-gray-400 mb-2">By occasion</p>
                  <div className="space-y-1.5">
                    {Object.entries(data.gifts_by_occasion)
                      .sort((a, b) => b[1] - a[1])
                      .map(([occasion, count]) => (
                        <div
                          key={occasion}
                          className="flex justify-between items-center"
                        >
                          <span className="text-sm text-gray-600">
                            {OCCASION_LABELS[occasion] ?? occasion}
                          </span>
                          <span className="text-sm font-semibold text-gray-900">
                            {count}
                          </span>
                        </div>
                      ))}
                  </div>
                </div>
              ) : (
                <p className="text-xs text-gray-400 mb-4">No gift data yet</p>
              )}
              {data.gifts_by_tier && Object.keys(data.gifts_by_tier).length > 0 && (
                <div>
                  <p className="text-xs text-gray-400 mb-2">By tier</p>
                  <div className="space-y-1.5">
                    {Object.entries(data.gifts_by_tier)
                      .sort((a, b) => b[1] - a[1])
                      .map(([tier, count]) => (
                        <div
                          key={tier}
                          className="flex justify-between items-center"
                        >
                          <span className="text-sm text-gray-600">
                            {TIER_LABELS[tier] ?? tier}
                          </span>
                          <span className="text-sm font-semibold text-gray-900">
                            {count}
                          </span>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Row 4 — invites */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="bg-white rounded-lg border border-gray-200 p-5">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-4">
                Tribe invites
              </p>
              <div className="space-y-3">
                <div className="flex justify-between items-baseline">
                  <span className="text-sm text-gray-600">Sent this month</span>
                  <span className="text-xl font-bold text-gray-900">
                    {data.invites_sent_this_month}
                  </span>
                </div>
                <div className="flex justify-between items-baseline border-t border-gray-100 pt-3">
                  <span className="text-sm text-gray-600">
                    Acceptance rate (all time)
                  </span>
                  <span
                    className={`text-xl font-bold ${
                      data.invite_acceptance_rate === null
                        ? "text-gray-400"
                        : data.invite_acceptance_rate >= 50
                        ? "text-green-600"
                        : data.invite_acceptance_rate >= 25
                        ? "text-amber-500"
                        : "text-red-500"
                    }`}
                  >
                    {data.invite_acceptance_rate === null
                      ? "—"
                      : `${data.invite_acceptance_rate}%`}
                  </span>
                </div>
              </div>
            </div>

            {/* Row 5 — churn signal */}
            <div className="bg-white rounded-lg border border-gray-200 p-5">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-4">
                Churn signal
              </p>
              <div className="flex justify-between items-baseline">
                <span className="text-sm text-gray-600">
                  Downgrades + cancellations this month
                </span>
                <span className="text-xl font-bold text-gray-900">
                  {data.cancellations_this_month}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
